import { Select } from '@creditornot/cb-select';
import React, { useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { UserRequirement } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';

import { BaseField } from './types';
import { useWoltSpecials } from '../../../context/WoltSpecialsContext';
import { FormRowRenderer } from '../FormRowRenderer';
import { DisplayText, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { AvailabilityDetails, FormValues } from '../types';

export const FirstTimeCustomers = ({ sidePanelMode, offer }: BaseField) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { values } = useFormState<FormValues>();
  const intl = useIntl();
  const { woltSpecialsCategory } = useWoltSpecials();

  const userRequirementOptions: { value: UserRequirement; label: string }[] = useMemo(
    () => [
      { value: 'no_wolt_purchases', label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-wolt' }) },
      {
        value: 'no_franchise_purchases',
        label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-franchise' }),
      },
      {
        value: 'no_venue_purchases',
        label: intl.formatMessage({ id: 'menu-item.ftu-restriction-values.ftu-venue' }),
      },
    ],
    [intl],
  );

  const previewComponent = (
    <DisplayText dir="auto">
      {offer?.user_requirements && offer?.user_requirements?.length > 0 ? (
        <>
          {offer.user_requirements.includes('no_wolt_purchases') && (
            <div>
              <FormattedMessage id="menu-item.ftu-restriction-values.ftu-wolt" />
            </div>
          )}
          {offer.user_requirements.includes('no_venue_purchases') && (
            <div>
              <FormattedMessage id="menu-item.ftu-restriction-values.ftu-venue" />
            </div>
          )}
          {offer.user_requirements.includes('no_franchise_purchases') && (
            <div>
              <FormattedMessage id="menu-item.ftu-restriction-values.ftu-franchise" />
            </div>
          )}
        </>
      ) : (
        <FormattedMessage id="menu-item.ftu-restriction-none" />
      )}
    </DisplayText>
  );

  const editComponent = (
    <Field<AvailabilityDetails['userRequirement']> name="userRequirement">
      {field => (
        <StyledInputContainer>
          <Select {...field.input} disabled={!menuPermissions.contentEdit} clearable options={userRequirementOptions} />
        </StyledInputContainer>
      )}
    </Field>
  );

  const isWoltSpecialsCategory =
    typeof woltSpecialsCategory?.id === 'string' && values.categoryId === woltSpecialsCategory?.id;

  if (!isWoltSpecialsCategory) {
    return null;
  }

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.ftu-restriction-label' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      shouldDisplayCreateTag={!values.userRequirement}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
